import { Service, signal } from '@angular/core';

import type { BottomSheetComponent } from './bottom-sheet.component';

/**
 * FIFO queue for kala-mobile bottom sheets.
 *
 *  This service is used to manage the queue of bottom sheets and open the next sheet when the current sheet is closed.
 */
@Service()
export class BottomSheetService {
	private readonly queue: BottomSheetComponent[] = [];
	private readonly activeSheet = signal<BottomSheetComponent | null>(null);
	private readonly pendingResolvers = new Map<BottomSheetComponent, Array<(value: void) => void>>();

	readonly current = this.activeSheet.asReadonly();

	/**
	 * Request that `sheet` open. Opens immediately if nothing is active;
	 * otherwise waits in the queue until the active sheet is dequeued.
	 */
	enqueue(sheet: BottomSheetComponent): Promise<void> {
		if (this.activeSheet() === sheet) {
			return Promise.resolve();
		}

		return new Promise<void>(resolve => {
			const resolvers = this.pendingResolvers.get(sheet) ?? [];
			resolvers.push(resolve);
			this.pendingResolvers.set(sheet, resolvers);

			if (!this.queue.includes(sheet)) {
				this.queue.push(sheet);
			}

			if (!this.activeSheet()) {
				this.openNext();
			}
		});
	}

	/**
	 * Remove `sheet` from the queue / active slot.
	 * When the active sheet is removed, the next queued sheet is opened.
	 */
	dequeue(sheet: BottomSheetComponent): void {
		const queueIndex = this.queue.indexOf(sheet);
		if (queueIndex !== -1) {
			this.queue.splice(queueIndex, 1);
		}

		this.resolvePending(sheet);

		if (this.activeSheet() === sheet) {
			this.activeSheet.set(null);
			this.openNext();
		}
	}

	/** Close the currently open sheet, if any. */
	close(): Promise<void> {
		const sheet = this.activeSheet();
		return sheet ? sheet.close() : Promise.resolve();
	}

	isAnyOpen(): boolean {
		return this.activeSheet()?.isOpen() ?? false;
	}

	private async openNext(): Promise<void> {
		const next = this.queue.shift() ?? null;
		this.activeSheet.set(next);

		if (!next) {
			return;
		}

		await next.present();
		this.resolvePending(next);
	}

	private resolvePending(sheet: BottomSheetComponent): void {
		const resolvers = this.pendingResolvers.get(sheet);
		if (!resolvers?.length) {
			return;
		}
		this.pendingResolvers.delete(sheet);
		for (const resolve of resolvers) {
			resolve();
		}
	}
}
